import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Megaphone, GoogleLogo, MetaLogo, TiktokLogo, TwitterLogo, PlayPause, Sparkle, Sliders, Trash } from '@phosphor-icons/react';
import { MUSIC_AUDIENCE_CATALOG, INTEREST_RECOMMENDATIONS } from '@/data/musicAudienceCatalog';

const PLAT = {
  google: { name: 'Google Ads', icon: GoogleLogo, color: 'text-blue-400', desc: 'YouTube · Search · Display' },
  meta:   { name: 'Meta Ads',   icon: MetaLogo,   color: 'text-blue-500', desc: 'Instagram · Facebook' },
  tiktok: { name: 'TikTok Ads', icon: TiktokLogo, color: 'text-[#ccff00]', desc: 'For You · Reels-like feed' },
  x:      { name: 'X Ads',      icon: TwitterLogo, color: 'text-zinc-200', desc: 'X · promoted posts' },
};

const emptyForm = () => ({
  name: '', platform: 'meta', goal: 'spotify_listeners',
  budget_usd: 50, target_country: 'US', target_locations: [{ type: 'country', name: 'United States', code: 'US' }], target_age: '18-34',
  interests: '', song_id: '', mode: 'auto', ad_hook: '',
  ai_targeting: null,
  account_mode: 'own',
  ad_account_id: '',
});

export default function Ads() {
  const [campaigns, setCampaigns] = useState([]);
  const [songs, setSongs] = useState([]);
  const [cfg, setCfg] = useState(null);  // platform-goal config from backend
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm());
  const [suggesting, setSuggesting] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);
  const [accountOptions, setAccountOptions] = useState(null);
  const [statusAccountOptions, setStatusAccountOptions] = useState({});
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const locationPickerRef = useRef(null);
  const [interestQuery, setInterestQuery] = useState('');
  const [interestOpen, setInterestOpen] = useState(false);
  const interestPickerRef = useRef(null);
  const [syncing, setSyncing] = useState('');
  const [adWallet, setAdWallet] = useState({
    balance_usd: 0,
    reserved_usd: 0,
    available_usd: 0,
    own: {
      balance_usd: 0,
      reserved_usd: 0,
      available_usd: 0,
    },
    platform: {
      balance_usd: 0,
      reserved_usd: 0,
      available_usd: 0,
    },
  });
  const [fundAmount, setFundAmount] = useState('100');
  const [funding, setFunding] = useState(false);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [fundAccountMode, setFundAccountMode] = useState('own');

  const load = async () => {
    const results = await Promise.allSettled([
      api.get('/campaigns'),
      api.get('/songs'),
      api.get('/ad-wallet'),
    ]);

    if (results[0].status === 'fulfilled') {
      setCampaigns(results[0].value.data);
    }

    if (results[1].status === 'fulfilled') {
      setSongs(results[1].value.data);
    }

    if (results[2].status === 'fulfilled') {
      setAdWallet(results[2].value.data);
    }
  };
  useEffect(() => {
    load();
    api.get('/campaigns/platform-config').then(r => setCfg(r.data));
    api.get('/campaigns/live-status').then(r => setLiveStatus(r.data));

    Promise.all(
      ['google', 'meta', 'tiktok', 'x'].map(async (platform) => {
        try {
          const { data } = await api.get('/campaigns/account-options', {
            params: { platform },
          });
          return [platform, data];
        } catch (err) {
          return [platform, null];
        }
      })
    ).then((entries) => {
      setStatusAccountOptions(Object.fromEntries(entries));
    });
  }, []);

  const addFunds = async () => {
    const amount = Number(fundAmount);

    if (!Number.isFinite(amount) || amount < 5 || amount > 10000) {
      toast.error('Enter an amount between $5 and $10,000');
      return;
    }

    setFunding(true);

    try {
      const { data } = await api.post('/payments/ad-wallet', {
        amount_usd: +amount.toFixed(2),
        account_mode: fundAccountMode,
        origin_url: window.location.origin,
      });

      if (!data?.checkout_url) {
        throw new Error('Stripe checkout URL was not returned');
      }

      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        e.message ||
        'Could not open Stripe checkout'
      );
    } finally {
      setFunding(false);
    }
  };


  const removeCampaign = async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Campaign deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const sync = async (id) => {
    setSyncing(id);
    try {
      await api.post(`/campaigns/${id}/sync`);
      toast.success('Delivery numbers refreshed from the ad platform');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(''); }
  };

  // Account selection is shared across all advertising platforms.
  // Changing platform or account mode resets the selected account and then
  // loads the accounts actually available for that route.
  useEffect(() => {
    let cancelled = false;

    setAccountOptions(null);
    setF(prev => ({
      ...prev,
      ad_account_id: '',
    }));

    api.get('/campaigns/account-options', {
      params: { platform: f.platform },
    }).then(({ data }) => {
      if (cancelled) return;

      setAccountOptions(data);

      const accounts = data?.[f.account_mode]?.accounts || [];
      const firstId = accounts.length
        ? String(accounts[0]?.id || '')
        : '';

      setF(prev => ({
        ...prev,
        ad_account_id: firstId,
      }));
    }).catch(() => {
      if (cancelled) return;

      setAccountOptions(null);
      setF(prev => ({
        ...prev,
        ad_account_id: '',
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [f.platform, f.account_mode]);

  // Goals allowed for the currently selected platform
  const allowedGoals = useMemo(
    () => cfg?.platforms.find(p => p.id === f.platform)?.goals || [],
    [cfg, f.platform]
  );

  const adBudget = Math.max(0, Number(f.budget_usd) || 0);
  const platformFeePct = f.account_mode === 'platform' ? 8 : 2;
  const platformFee = +(adBudget * platformFeePct / 100).toFixed(2);

  // Campaign launch consumes only advertising funds.
  // The platform fee is charged when the funds are purchased.
  const campaignAvailableBalance =
    f.account_mode === 'platform'
      ? Number(adWallet.platform?.available_usd || 0)
      : Number(adWallet.own?.available_usd || 0);

  const insufficientCampaignBalance =
    f.account_mode === 'platform' && adBudget > campaignAvailableBalance;

  const selectedAdsStatus = liveStatus?.[f.platform] || {};
  const ownAdsStatus = selectedAdsStatus.own_account || {};
  const platformAdsStatus = selectedAdsStatus.platform_account || {};

  // Reset goal if it isn't allowed on the newly picked platform
  useEffect(() => {
    if (allowedGoals.length && !allowedGoals.includes(f.goal)) {
      setF(prev => ({ ...prev, goal: allowedGoals[0], ai_targeting: null }));
    }
  }, [allowedGoals, f.goal]);

  const suggest = async () => {
    if (!f.song_id) { toast.error('Pick a song so AI has something to target'); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post('/campaigns/ai-suggest', {
        song_id: f.song_id, platform: f.platform, budget_usd: +f.budget_usd, goal: f.goal,
      });
      setF(prev => ({
        ...prev,
        ai_targeting: data,
        target_country: (data.target_countries || []).join(', ') || prev.target_country,
        target_locations: (data.target_countries || []).map(code => ({ type: 'country', name: code, code, country_code: code })),
        target_age: data.target_age || prev.target_age,
        interests: (data.interests || []).join(', '),
        ad_hook: data.ad_hook || '',
      }));
      toast.success('AI picked your audience — review below and launch');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggest failed');
    } finally { setSuggesting(false); }
  };

  useEffect(() => {
    const handleOutsideLocationClick = (event) => {
      if (
        locationPickerRef.current &&
        !locationPickerRef.current.contains(event.target)
      ) {
        setLocationOpen(false);
      }
    };

    const handleLocationEscape = (event) => {
      if (event.key === 'Escape') {
        setLocationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideLocationClick);
    document.addEventListener('keydown', handleLocationEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideLocationClick);
      document.removeEventListener('keydown', handleLocationEscape);
    };
  }, []);

  useEffect(() => {
    const handleOutsideInterestClick = (event) => {
      if (
        interestPickerRef.current &&
        !interestPickerRef.current.contains(event.target)
      ) {
        setInterestOpen(false);
      }
    };

    const handleInterestEscape = (event) => {
      if (event.key === 'Escape') {
        setInterestOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideInterestClick);
    document.addEventListener('keydown', handleInterestEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInterestClick);
      document.removeEventListener('keydown', handleInterestEscape);
    };
  }, []);

  const popularCountries = [
    ['United States', 'US'],
    ['Romania', 'RO'],
    ['United Kingdom', 'GB'],
    ['Germany', 'DE'],
    ['France', 'FR'],
    ['Spain', 'ES'],
    ['Italy', 'IT'],
    ['Canada', 'CA'],
    ['Australia', 'AU'],
    ['Netherlands', 'NL'],
    ['Poland', 'PL'],
    ['United Arab Emirates', 'AE'],
  ].map(([name, code]) => ({
    type: 'country',
    name,
    code,
    country_code: code,
    target_type: 'Country',
  }));

  const searchLocations = async (value) => {
    setLocationQuery(value);
    setLocationOpen(true);

    if (!value.trim()) {
      setLocationResults(popularCountries);
      return;
    }

    if (value.trim().length < 2) {
      setLocationResults([]);
      return;
    }

    if (f.platform !== 'google') {
      const q = value.trim().toLowerCase();
      setLocationResults(
        popularCountries.filter((x) =>
          x.name.toLowerCase().includes(q) ||
          x.code.toLowerCase().includes(q)
        )
      );
      return;
    }

    setLocationSearching(true);

    try {
      const { data } = await api.get('/campaigns/location-search', {
        params: { q: value.trim() },
      });
      setLocationResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setLocationResults([]);
      toast.error(
        e.response?.data?.detail ||
        'Could not search locations'
      );
    } finally {
      setLocationSearching(false);
    }
  };

  const selectLocation = async (location) => {
    let selected = { ...location };

    // Google country entries from the local quick list get their real
    // GeoTargetConstant before being stored.
    if (
      f.platform === 'google' &&
      selected.type === 'country' &&
      !selected.resource_name
    ) {
      try {
        const { data } = await api.get('/campaigns/location-search', {
          params: {
            q: selected.name,
            country_code: selected.code,
          },
        });

        selected =
          (data || []).find(
            (item) =>
              item.type === 'country' &&
              item.country_code === selected.code
          ) || selected;
      } catch (_) {
        // The country code remains usable through legacy target_country.
      }
    }

    const exists = (f.target_locations || []).some((item) => (
      item.resource_name &&
      selected.resource_name &&
      item.resource_name === selected.resource_name
    ) || (
      item.type === selected.type &&
      (item.code || item.country_code) ===
        (selected.code || selected.country_code) &&
      item.name === selected.name
    ));

    if (exists) {
      setLocationQuery('');
      return;
    }

    const next = [...(f.target_locations || []), selected];

    const countryCodes = [...new Set(
      next
        .map((item) =>
          item.type === 'country'
            ? item.code
            : item.country_code
        )
        .filter(Boolean)
    )];

    setF({
      ...f,
      target_locations: next,
      target_country: countryCodes.join(', ') || f.target_country,
    });

    setLocationQuery('');
    setLocationResults([]);
    setLocationOpen(false);
  };

  const removeLocation = (index) => {
    const next = (f.target_locations || []).filter((_, i) => i !== index);

    const countryCodes = [...new Set(
      next
        .map((item) =>
          item.type === 'country'
            ? item.code
            : item.country_code
        )
        .filter(Boolean)
    )];

    setF({
      ...f,
      target_locations: next,
      target_country: countryCodes.join(', ') || 'US',
    });
  };

  const selectedInterests = (f.interests || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);

  const filteredInterestGroups = useMemo(() => {
    const query = interestQuery.trim().toLowerCase();

    return MUSIC_AUDIENCE_CATALOG
      .map(group => ({
        ...group,
        items: query
          ? group.items.filter(item =>
              item.toLowerCase().includes(query)
            )
          : group.items,
      }))
      .filter(group => group.items.length > 0);
  }, [interestQuery]);

  const setSelectedInterests = (values) => {
    setF({
      ...f,
      interests: [...new Set(values)].join(', '),
    });
  };

  const addInterest = (interest) => {
    const value = String(interest || '').trim();
    if (!value) return;

    setSelectedInterests([...selectedInterests, value]);
    setInterestQuery('');
    setInterestOpen(false);
  };

  const removeInterest = (interest) => {
    setSelectedInterests(
      selectedInterests.filter(item => item.toLowerCase() !== interest.toLowerCase())
    );
  };

  const recommendInterests = () => {
    const recommended = INTEREST_RECOMMENDATIONS[f.goal] || [
      'Music Discovery',
      'New Music',
      'Music Fans',
      'Music Streaming',
    ];

    setSelectedInterests([
      ...selectedInterests,
      ...recommended,
    ]);
  };

  const handleInterestFocus = () => {
    setInterestOpen(true);
  };

  const handleInterestKeyDown = (event) => {
    if (event.key === 'Enter' && interestQuery.trim()) {
      event.preventDefault();
      addInterest(interestQuery.trim());
    }
  };

  const create = async () => {
    if (!f.name) { toast.error('Name your campaign'); return; }

    const selectedAccountSection = accountOptions?.[f.account_mode];

    if (!selectedAccountSection?.available) {
      toast.error(
        selectedAccountSection?.reason ||
        'No advertising account is available for this route'
      );
      return;
    }

    if (!f.ad_account_id) {
      toast.error('Select an advertising account');
      return;
    }

    if (f.mode === 'auto' && !f.ai_targeting) {
      toast.error('Click "Let AI target" first');
      return;
    }
    try {
      await api.post('/campaigns', {
        ...f,
        account_mode: f.account_mode,
        ad_account_id: f.ad_account_id || undefined,
        target_locations: f.target_locations || [],
        budget_usd: +f.budget_usd,
        daily_budget: +f.budget_usd / 7,
        interests: f.interests.split(',').map(x => x.trim()).filter(Boolean),
        song_id: f.song_id || undefined,
      });
      setOpen(false); setF(emptyForm());
      toast.success('Campaign draft created — review it before launch');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to launch');
    }
  };

  const toggle = async (campaign) => {
    try {
      if (campaign.status === 'draft') {
        await api.post(`/campaigns/${campaign.campaign_id}/start`);
        toast.success('Campaign launch started');
      } else {
        const { data } = await api.post(`/campaigns/${campaign.campaign_id}/toggle`);
        toast.success(`Campaign ${data.status}`);
      }

      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        'Could not start or update the campaign'
      );
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex flex-col items-stretch gap-5">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Ad Campaigns</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Cheap ads. Real listeners.</h1>
          <p className="text-zinc-400 mt-1 text-sm">Google, Meta, TikTok or X — pick your platform + budget, let AI target for you, or dial it in yourself.</p>
        </div>
        <div className="mb-0 w-full max-w-3xl border border-zinc-800 rounded-xl bg-zinc-900/30 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Advertising funds
                </div>

                <div className="font-display text-2xl mt-1">
                  ${Number(adWallet.available_usd || 0).toFixed(2)}
                  <span className="text-sm font-mono text-zinc-500 ml-2">
                    available
                  </span>
                </div>

                {Number(adWallet.reserved_usd || 0) > 0 && (
                  <div className="text-[10px] font-mono text-zinc-500 mt-1">
                    ${Number(adWallet.reserved_usd).toFixed(2)} reserved
                  </div>
                )}
              </div>

              <Button
                data-testid="ad-wallet-add-btn"
                onClick={() => setFundDialogOpen(true)}
                className="btn-neon rounded-full whitespace-nowrap"
              >
                + Add funds
              </Button>
            </div>
          </div>

          <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">
                  Add advertising funds
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Advertising account
                  </label>

                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      data-testid="ad-fund-account-own"
                      onClick={() => setFundAccountMode('own')}
                      className={`p-3 rounded-lg border text-left ${
                        fundAccountMode === 'own'
                          ? 'border-[#ccff00] bg-[#ccff00]/10'
                          : 'border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-display text-sm">
                        My ad account
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        Use your connected Meta / Google account
                      </div>
                      <div className="text-[10px] font-mono text-[#ccff00] mt-1">
                        TuneAmplifier fee · 2%
                      </div>
                    </button>

                    <button
                      type="button"
                      data-testid="ad-fund-account-platform"
                      onClick={() => setFundAccountMode('platform')}
                      className={`p-3 rounded-lg border text-left ${
                        fundAccountMode === 'platform'
                          ? 'border-[#ccff00] bg-[#ccff00]/10'
                          : 'border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-display text-sm">
                        TuneAmplifier Ad account
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        We manage the advertising account
                      </div>
                      <div className="text-[10px] font-mono text-[#ccff00] mt-1">
                        TuneAmplifier fee · 8%
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Advertising funds
                  </label>

                  <Input
                    data-testid="ad-wallet-amount"
                    type="number"
                    min="5"
                    max="10000"
                    step="1"
                    value={fundAmount}
                    onChange={e => setFundAmount(e.target.value)}
                    className="mt-2 bg-zinc-950 border-zinc-800"
                  />

                  <div className="flex flex-wrap gap-2 mt-2">
                    {[5, 50, 100, 250].map(amount => (
                      <Button
                        key={amount}
                        type="button"
                        variant="outline"
                        onClick={() => setFundAmount(String(amount))}
                        className="rounded-full border-zinc-800 bg-transparent text-xs"
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                </div>

                {(() => {
                  const amount = Math.max(0, Number(fundAmount) || 0);
                  const feePct = fundAccountMode === 'platform' ? 8 : 2;
                  const fee = +(amount * feePct / 100).toFixed(2);
                  const total = +(amount + fee).toFixed(2);

                  return (
                    <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30 text-xs font-mono">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">
                          Advertising funds
                        </span>
                        <span>${amount.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between mt-1">
                        <span className="text-zinc-500">
                          TuneAmplifier fee ({feePct}%)
                        </span>
                        <span>${fee.toFixed(2)}</span>
                      </div>

                      <div className="border-t border-zinc-800 mt-2 pt-2 flex justify-between text-sm">
                        <span className="text-zinc-300">
                          Total charged
                        </span>
                        <span className="text-[#ccff00]">
                          ${total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFundDialogOpen(false)}
                  className="rounded-full border-zinc-800"
                >
                  Cancel
                </Button>

                <Button
                  data-testid="ad-fund-checkout-btn"
                  type="button"
                  onClick={addFunds}
                  disabled={funding}
                  className="btn-neon rounded-full"
                >
                  {funding ? 'Opening checkout…' : 'Continue to Stripe'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="ads-new-btn" className="btn-neon rounded-full h-10 font-display ml-auto">+ New campaign</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Launch a campaign</DialogTitle></DialogHeader>

            {/* Platform picker */}
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Platform · optimized CPM</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {(cfg?.platforms || Object.entries(PLAT).map(([k, v]) => ({ id: k, name: v.name, sub: v.desc }))).map(p => {
                  const Icon = (PLAT[p.id] || PLAT.meta).icon;
                  const color = (PLAT[p.id] || PLAT.meta).color;
                  const on = f.platform === p.id;
                  return (
                    <button
                      key={p.id} data-testid={`ad-platform-${p.id}`}
                      onClick={() => setF({ ...f, platform: p.id, ai_targeting: null })}
                      className={`p-3 rounded-lg border text-left transition-[background-color,border-color] ${
                        on ? 'border-[#ccff00] bg-[#ccff00]/10' : 'border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <Icon size={18} className={color} weight="fill" />
                      <div className="font-display text-sm mt-1">{p.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500">{p.sub}</div>
                      {p.cpm_usd && <div className="text-[10px] font-mono text-[#ccff00] mt-0.5">from ${p.cpm_usd} CPM</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Advertising account */}
            <div className="mt-4">
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                Advertising account
              </label>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  data-testid="ad-account-own"
                  disabled={!accountOptions?.own?.available}
                  onClick={() => setF(prev => ({
                    ...prev,
                    account_mode: 'own',
                    ad_account_id: '',
                  }))}
                  className={`p-3 rounded-lg border text-left transition-[background-color,border-color,opacity] ${
                    f.account_mode === 'own'
                      ? 'border-[#ccff00] bg-[#ccff00]/10'
                      : 'border-zinc-800 hover:border-zinc-600'
                  } ${!accountOptions?.own?.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-display text-sm">Use my account</div>

                  <div className="text-[10px] font-mono text-zinc-500 mt-1">
                    You pay the ad platform directly
                  </div>

                  <div className={`text-[10px] font-mono mt-1 ${
                    accountOptions?.own?.available
                      ? 'text-emerald-400'
                      : 'text-amber-400'
                  }`}>
                    {accountOptions?.own?.available
                      ? `${accountOptions.own.accounts.length} account${accountOptions.own.accounts.length === 1 ? '' : 's'} available`
                      : (accountOptions?.own?.reason || 'Checking account availability...')}
                  </div>

                  <div className="text-[10px] font-mono text-[#ccff00] mt-1">
                    TuneAmplifier fee · 2%
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="ad-account-platform"
                  disabled={!accountOptions?.platform?.available}
                  onClick={() => setF(prev => ({
                    ...prev,
                    account_mode: 'platform',
                    ad_account_id: '',
                  }))}
                  className={`p-3 rounded-lg border text-left transition-[background-color,border-color,opacity] ${
                    f.account_mode === 'platform'
                      ? 'border-[#ccff00] bg-[#ccff00]/10'
                      : 'border-zinc-800 hover:border-zinc-600'
                  } ${!accountOptions?.platform?.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-display text-sm">Use TuneAmplifier</div>

                  <div className="text-[10px] font-mono text-zinc-500 mt-1">
                    Prepaid advertising balance required
                  </div>

                  <div className={`text-[10px] font-mono mt-1 ${
                    accountOptions?.platform?.available
                      ? 'text-emerald-400'
                      : 'text-amber-400'
                  }`}>
                    {accountOptions?.platform?.available
                      ? (accountOptions.platform.accounts?.[0]?.name || 'Managed account ready')
                      : (accountOptions?.platform?.reason || 'Checking account availability...')}
                  </div>

                  <div className="text-[10px] font-mono text-[#ccff00] mt-1">
                    TuneAmplifier fee · 8%
                  </div>
                </button>
              </div>

              {/* Own-account selector */}
              {f.account_mode === 'own' && (
                <div className="mt-2 p-3 rounded-lg border border-zinc-800 bg-zinc-900/30">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
                    Select advertising account
                  </div>

                  {accountOptions?.own?.available ? (
                    <Select
                      value={f.ad_account_id || ''}
                      onValueChange={v =>
                        setF(prev => ({
                          ...prev,
                          ad_account_id: v,
                        }))
                      }
                    >
                      <SelectTrigger
                        data-testid="ad-account-select"
                        className="bg-zinc-950 border-zinc-800"
                      >
                        <SelectValue placeholder="Select an advertising account" />
                      </SelectTrigger>

                      <SelectContent>
                        {(accountOptions.own.accounts || []).map(account => (
                          <SelectItem
                            key={account.id}
                            value={String(account.id)}
                          >
                            {account.name || account.id}
                            {account.currency ? ` · ${account.currency}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-[11px] font-mono text-amber-400">
                      {accountOptions?.own?.reason || 'No advertising account available'}
                    </div>
                  )}
                </div>
              )}

              {/* TuneAmplifier managed account */}
              {f.account_mode === 'platform' && (
                <div className="mt-2 p-3 rounded-lg border border-zinc-800 bg-zinc-900/30">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    Managed advertising account
                  </div>

                  <div className="mt-1 text-sm font-display text-zinc-200">
                    {accountOptions?.platform?.accounts?.[0]?.name ||
                      'TuneAmplifier managed account'}
                  </div>

                  {accountOptions?.platform?.accounts?.[0]?.id && (
                    <div className="mt-1 text-[10px] font-mono text-zinc-500">
                      Account ID · {accountOptions.platform.accounts[0].id}
                    </div>
                  )}

                  <div className="mt-2 text-[11px] font-mono text-zinc-400">
                    This account is selected automatically.
                  </div>
                </div>
              )}

              {(f.account_mode === 'platform' || f.account_mode === 'own') && (
                <div className="mt-2 p-3 rounded-lg border border-zinc-800 bg-zinc-900/30 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Ad budget</span>
                    <span>${adBudget.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between mt-1">
                    <span className="text-zinc-500">
                      TuneAmplifier fee ({platformFeePct}%)
                    </span>
                    <span>${platformFee.toFixed(2)}</span>
                  </div>

                  {insufficientCampaignBalance && (
                    <div className="mt-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <div className="text-amber-400">
                        Insufficient advertising funds
                      </div>

                      <div className="text-zinc-500 mt-1">
                        Add funds before launching this campaign.
                      </div>

                      <Button
                        type="button"
                        onClick={() => {
                          const missing = Math.max(
                            5,
                            Math.ceil(
                              adBudget - campaignAvailableBalance
                            )
                          );

                          setFundAmount(String(missing));
                          setFundAccountMode(f.account_mode);
                          setFundDialogOpen(true);
                        }}
                        className="mt-2 h-8 rounded-full text-xs"
                      >
                        Add funds
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="ad-name-input" placeholder="Campaign name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="col-span-2 bg-zinc-950 border-zinc-800" />
              <Select value={f.goal} onValueChange={v => setF({ ...f, goal: v, ai_targeting: null })}>
                <SelectTrigger data-testid="ad-goal-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(allowedGoals.length ? allowedGoals : ['spotify_listeners']).map(g => (
                    <SelectItem key={g} value={g}>{cfg?.goal_labels?.[g] || g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="ad-budget-input" type="number" min="5" placeholder="Budget $ (total)" value={f.budget_usd} onChange={e => setF({ ...f, budget_usd: e.target.value, ai_targeting: null })} className="bg-zinc-950 border-zinc-800" />
              <Select value={f.song_id} onValueChange={v => setF({ ...f, song_id: v, ai_targeting: null })}>
                <SelectTrigger data-testid="ad-song-select" className="col-span-2 bg-zinc-950 border-zinc-800"><SelectValue placeholder="Attach song (recommended for AI targeting)" /></SelectTrigger>
                <SelectContent>
                  {songs.map(s => <SelectItem key={s.song_id} value={s.song_id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Mode tabs */}
            <Tabs value={f.mode} onValueChange={v => setF({ ...f, mode: v })} className="tabs-underline mt-2">
              <TabsList className="bg-transparent border-b border-zinc-800 rounded-none w-full justify-start h-auto p-0 gap-6">
                <TabsTrigger data-testid="ad-mode-auto" value="auto" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">
                  <Sparkle size={12} className="mr-1.5" /> Auto (AI targeting)
                </TabsTrigger>
                <TabsTrigger data-testid="ad-mode-advanced" value="advanced" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">
                  <Sliders size={12} className="mr-1.5" /> Advanced
                </TabsTrigger>
              </TabsList>

              <TabsContent value="auto" className="mt-4">
                <Button data-testid="ad-ai-suggest-btn" onClick={suggest} disabled={suggesting} className="w-full btn-neon rounded-full font-display">
                  <Sparkle size={14} weight="fill" className="mr-2" />
                  {suggesting ? 'AI is picking your audience…' : (f.ai_targeting ? 'Re-generate AI targeting' : 'Let AI target for you')}
                </Button>
                {f.ai_targeting && (
                  <div className="mt-4 p-4 rounded-lg border border-[#ccff00]/30 bg-[#ccff00]/5 space-y-2 text-sm">
                    <div className="font-mono text-[10px] uppercase text-[#ccff00]">AI-picked audience</div>
                    <div className="text-zinc-200">{f.ai_targeting.audience_desc}</div>
                    <div className="grid grid-cols-2 gap-3 text-xs font-mono pt-2 border-t border-[#ccff00]/20">
                      <div><span className="text-zinc-500">Countries:</span> <span className="text-zinc-200">{(f.ai_targeting.target_countries || []).join(', ')}</span></div>
                      <div><span className="text-zinc-500">Age:</span> <span className="text-zinc-200">{f.ai_targeting.target_age}</span></div>
                      <div className="col-span-2"><span className="text-zinc-500">Interests:</span> <span className="text-zinc-200">{(f.ai_targeting.interests || []).join(' · ')}</span></div>
                      <div className="col-span-2"><span className="text-zinc-500">Ad hook:</span> <span className="text-[#ccff00]">"{f.ai_targeting.ad_hook}"</span></div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="advanced" className="mt-4 space-y-5">

                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                    Target locations
                  </div>
                  <div className="text-xs text-zinc-600">
                    Choose the countries or cities where your ads should appear.
                  </div>

                  {(f.target_locations || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {(f.target_locations || []).map((location, index) => (
                        <button
                          key={`${location.name}-${index}`}
                          type="button"
                          onClick={() => removeLocation(index)}
                          className="group inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:border-[#ccff00]/50 hover:text-white"
                        >
                          <span>
                            {location.type === 'city'
                              ? `${location.name}, ${location.country_code || ''}`
                              : location.name}
                          </span>
                          <span className="text-zinc-600 group-hover:text-[#ccff00]">×</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div ref={locationPickerRef} className="relative">
                    <Input
                      value={locationQuery}
                      onChange={e => searchLocations(e.target.value)}
                      onFocus={() => {
                        setLocationOpen(true);
                        if (!locationQuery) setLocationResults(popularCountries);
                      }}
                      placeholder={
                        f.platform === 'google'
                          ? 'Search countries or cities...'
                          : 'Search countries...'
                      }
                      className="bg-zinc-950 border-zinc-800"
                    />

                    {locationOpen && (locationSearching || locationResults.length > 0) && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl">
                        {locationSearching && (
                          <div className="px-3 py-3 text-xs text-zinc-500 font-mono">
                            Searching locations…
                          </div>
                        )}

                        {!locationSearching && locationResults.map((location, index) => (
                          <button
                            key={`${location.resource_name || location.code || location.name}-${index}`}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              selectLocation(location);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-zinc-900 border-b border-zinc-900 last:border-0"
                          >
                            <div className="text-sm text-zinc-200">
                              {location.name}
                            </div>
                            <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
                              {location.type === 'city'
                                ? `${location.country_code || ''} · City`
                                : `${location.code || location.country_code || ''} · Country`}
                            </div>
                          </button>
                        ))}

                        {!locationSearching &&
                          locationQuery &&
                          locationResults.length === 0 && (
                            <div className="px-3 py-3 text-xs text-zinc-600">
                              No matching locations found.
                            </div>
                          )}
                      </div>
                    )}
                  </div>

                  <div className="text-[10px] font-mono text-zinc-700">
                    {f.platform === 'google'
                      ? 'Google Ads supports country and city targeting here.'
                      : 'Country targeting is available here. City targeting for this platform will be added when its location API is connected.'}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                    Age range
                  </div>
                  <div className="text-xs text-zinc-600">
                    Choose the age group of people who should see your ads.
                  </div>
                  <Input
                    value={f.target_age}
                    onChange={e => setF({ ...f, target_age: e.target.value })}
                    placeholder="18-34"
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <div className="text-[10px] font-mono text-zinc-700">
                    Example: 18-34 means people aged 18 through 34.
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                    Audience interests
                  </div>

                  <div className="text-xs text-zinc-600">
                    Choose music interests that match the people you want to reach.
                  </div>

                  {(selectedInterests.length > 0) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {selectedInterests.map((interest, index) => (
                        <button
                          key={`${interest}-${index}`}
                          type="button"
                          onClick={() => removeInterest(interest)}
                          className="group inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:border-[#ccff00]/50 hover:text-white"
                        >
                          <span>{interest}</span>
                          <span className="text-zinc-600 group-hover:text-[#ccff00]">×</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div ref={interestPickerRef} className="relative">
                    <div className="flex gap-2">
                      <Input
                        value={interestQuery}
                        onFocus={handleInterestFocus}
                        onChange={e => {
                          setInterestQuery(e.target.value);
                          setInterestOpen(true);
                        }}
                        onKeyDown={handleInterestKeyDown}
                        placeholder="Search music interests..."
                        className="bg-zinc-950 border-zinc-800"
                      />

                      <Button
                        type="button"
                        onClick={recommendInterests}
                        className="shrink-0 btn-neon rounded-lg text-xs px-3"
                      >
                        Recommend
                      </Button>
                    </div>

                    {interestOpen && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl">
                        {filteredInterestGroups.length > 0 ? (
                          filteredInterestGroups.map(group => (
                            <div key={group.category} className="border-b border-zinc-900 last:border-0">
                              <div className="px-3 pt-3 pb-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                {group.category}
                              </div>

                              <div className="pb-2">
                                {group.items.map(item => {
                                  const selected = selectedInterests.some(
                                    value => value.toLowerCase() === item.toLowerCase()
                                  );

                                  return (
                                    <button
                                      key={item}
                                      type="button"
                                      onClick={() => (
                                        selected
                                          ? removeInterest(item)
                                          : addInterest(item)
                                      )}
                                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                        selected
                                          ? 'bg-[#ccff00]/10 text-[#ccff00]'
                                          : 'text-zinc-300 hover:bg-zinc-900'
                                      }`}
                                    >
                                      <span>{selected ? '✓ ' : ''}{item}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3">
                            {interestQuery.trim() ? (
                              <button
                                type="button"
                                onClick={() => addInterest(interestQuery.trim())}
                                className="w-full text-left rounded-md px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900"
                              >
                                + Add custom interest: "{interestQuery.trim()}"
                              </button>
                            ) : (
                              <div className="text-xs text-zinc-600">
                                No interests found.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="text-[10px] font-mono text-zinc-700">
                    Search the library, choose multiple interests, or add your own.
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                    Ad headline
                  </div>
                  <div className="text-xs text-zinc-600">
                    Optional headline used in the advertisement.
                  </div>
                  <Input
                    value={f.ad_hook}
                    onChange={e => setF({ ...f, ad_hook: e.target.value })}
                    placeholder="Discover new music"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>

              </TabsContent>
            </Tabs>

            <DialogFooter><Button data-testid="ad-launch-btn" onClick={create} className="btn-neon rounded-full">Launch</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {liveStatus && (
        <div data-testid="ads-live-status" className="mb-6 border border-zinc-800 rounded-xl bg-zinc-900/30 p-4 flex flex-wrap gap-4">
          {Object.entries(liveStatus).map(([k, v]) => {
            const synced = statusAccountOptions[k];

            const ownReady = synced
              ? Boolean(synced.own?.available)
              : Boolean(v.own_account?.live);

            const platformReady = synced
              ? Boolean(synced.platform?.available)
              : Boolean(v.platform_account?.live);

            const state = v.state || (v.live === true ? 'live' : 'error');

            // Only an explicit backend confirmation can produce LIVE.
            const live = v.live === true && state === 'live';

            const statusLabels = {
              live: 'LIVE',
              configured: 'CONNECTED · NOT VERIFIED',
              not_connected: 'NOT CONNECTED',
              not_implemented: 'NOT IMPLEMENTED',
              pending: 'PENDING',
              pending_approval: 'PENDING APPROVAL',
              error: 'API ERROR',
              unavailable: 'UNAVAILABLE',
            };

            const statusColors = {
              live: 'text-emerald-400',
              configured: 'text-blue-400',
              not_connected: 'text-zinc-500',
              not_implemented: 'text-zinc-500',
              pending: 'text-amber-400',
              pending_approval: 'text-amber-400',
              error: 'text-red-400',
              unavailable: 'text-zinc-500',
            };

            const statusColor = statusColors[state] || statusColors.error;
            const statusLabel = statusLabels[state] || `UNKNOWN STATUS · ${state}`;

            return (
              <div key={k} className="flex items-start gap-2 text-xs font-mono min-w-[230px]">
                <span
                  className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                    live ? 'bg-emerald-400' :
                    ['pending', 'pending_approval'].includes(state) ? 'bg-amber-400' :
                    state === 'error' ? 'bg-red-400' :
                    state === 'configured' ? 'bg-blue-400' : 'bg-zinc-600'
                  }`}
                />
                <div>
                  <div className={`uppercase ${statusColor}`}>
                    {PLAT[k]?.name || k} · {statusLabel}
                  </div>

                  <div className="text-zinc-500 mt-1">
                    My account: {ownReady ? 'connected / selectable' : 'not connected'}
                  </div>

                  <div className="text-zinc-500">
                    TuneAmplifier: {platformReady ? 'configured / selectable' : 'not available'}
                  </div>

                  {!live && (
                    <div className="text-zinc-600 mt-0.5 max-w-[300px]">
                      {v.reason || synced?.own?.reason || synced?.platform?.reason}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {campaigns.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-xl p-12 text-center bg-zinc-900/20">
          <Megaphone size={32} weight="duotone" className="text-zinc-700 mx-auto" />
          <p className="mt-4 text-sm text-zinc-500 font-mono">no campaigns yet — launch your first one.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-in">
        {campaigns.map(c => {
          const P = PLAT[c.platform] || PLAT.meta;
          const Icon = P.icon;
          const ctr = c.impressions ? ((c.clicks / c.impressions) * 100).toFixed(2) : '0.00';
          const cpc = c.clicks ? (c.spend / c.clicks).toFixed(2) : '0.00';
          return (
            <div key={c.campaign_id} data-testid={`campaign-${c.campaign_id}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5 card-hover">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Icon size={14} className={P.color} weight="fill" />
                    <span className="text-xs font-mono text-zinc-500">{P.name}</span>
                    {c.mode === 'auto' && <span className="text-[10px] font-mono text-[#ccff00] border border-[#ccff00]/30 px-1.5 py-0.5 rounded">AI</span>}
                  </div>
                  <div className="font-display text-lg mt-1">{c.name}</div>
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                  c.status === 'live' ? 'bg-[#ccff00]/20 text-[#ccff00]' : 'bg-zinc-800 text-zinc-400'
                }`}>{c.status}</span>
                  <button data-testid={`delete-campaign-${c.campaign_id}`} onClick={() => removeCampaign(c.campaign_id)}
                    className="ml-2 text-zinc-600 hover:text-red-400 transition-colors" title="Delete campaign">
                    <Trash size={14} />
                  </button>
              </div>
              {c.ad_hook && <p className="text-sm italic text-zinc-300 mb-3 border-l-2 border-[#ccff00]/30 pl-3">"{c.ad_hook}"</p>}
              {c.media_url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${c.media_url}`} alt=""
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="w-full h-32 object-cover rounded-lg border border-zinc-800 mb-3" />
              )}
              {c.creative?.source === 'creative_studio' && (
                <div data-testid={`campaign-creative-${c.campaign_id}`} className="text-[10px] font-mono uppercase text-[#ccff00] border border-[#ccff00]/30 inline-block px-1.5 py-0.5 rounded mb-2">Creative Studio</div>
              )}
              <div data-testid={`campaign-mode-${c.campaign_id}`} className={`text-[10px] font-mono uppercase inline-block px-1.5 py-0.5 rounded mb-2 ml-1 ${
                c.live_mode === 'real' ? 'text-emerald-400 border border-emerald-400/40' : 'text-zinc-500 border border-zinc-700'
              }`}>{c.live_mode === 'real' ? 'live spend' : 'simulated'}</div>
              <div className="grid grid-cols-3 gap-2 text-center my-4">
                <div><div className="font-display text-xl">{c.impressions.toLocaleString()}</div><div className="text-[10px] font-mono text-zinc-500 uppercase">Impress.</div></div>
                <div><div className="font-display text-xl">{c.clicks.toLocaleString()}</div><div className="text-[10px] font-mono text-zinc-500 uppercase">Clicks</div></div>
                <div><div className="font-display text-xl text-[#ccff00]">{c.conversions}</div><div className="text-[10px] font-mono text-zinc-500 uppercase">Listeners</div></div>
              </div>
              <div className="flex items-center justify-between text-xs font-mono text-zinc-400 border-t border-zinc-800 pt-3">
                <span>CTR {ctr}%</span>
                <span>CPC ${cpc}</span>
                <span>${c.spend.toFixed(0)} / ${c.budget_usd}</span>
              </div>
              <button
                onClick={() => toggle(c)}
                data-testid={`toggle-${c.campaign_id}`}
                className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-zinc-400 hover:text-[#ccff00] py-2 border border-zinc-800 rounded-md transition-colors"
              >
                <PlayPause size={14} />
                {c.status === 'draft' ? 'Launch' : (c.status === 'live' ? 'Pause' : 'Resume')}
              </button>
              {c.live_mode === 'real' && c.platform === 'meta' && (
                <button onClick={() => sync(c.campaign_id)} disabled={syncing === c.campaign_id} data-testid={`sync-${c.campaign_id}`}
                  className="mt-2 w-full text-xs font-mono text-emerald-400 hover:underline py-1">
                  {syncing === c.campaign_id ? 'syncing…' : 'Sync real delivery'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
